import React, { useRef, useMemo } from "react";
import { EMPTY_STATE_SUBTITLE, EMPTY_STATE_TITLE, NOTIFICATIONS_PANEL_TITLE, SECTION_TYPE } from "./NotificationsPanel.consts";
import NotificationItem from "../notification-item/NotificationItem";
import CloseIcon from "src/icons/CloseIcon";
import { Header, PanelContainer, Title, Content, EmptyStateContainer, EmptyStateSubtitle, EmptyStateTitle, VirtualizedListContainer, VirtualizedRow, VirtualizedHeader } from "./NotificationsPanel.styles";
import useNotificationsPanel from "./NotificationsPanel.hook";
import { Drawer, Skeleton, useTheme } from "@mui/material";
import EmptyStateIcon from "src/icons/notifications/EmptyStateIcon";
import NotificationItemSkeleton from "../notification-item/NotificationItemSkeleton";
import { HyroIconButton } from "@hyro-components";
import { ICON_BUTTON_VARIANTS, ICON_BUTTON_SIZES } from "@hyro-components/hyro-icon-button/HyroIconButton.consts";
import HyroButton from "@hyro-components/hyro-button/HyroButton";
import { BUTTON_COLORS } from "@hyro-components/hyro-button/HyroButton.consts";
import ChevronLeftIcon from "src/icons/ChevronLeft";
import NotificationDetailedView from "../notification-detailed-view/NotificationDetailedView";

const NotificationsPanel = ({ open, onClose }) => {
  const theme = useTheme();
  const parentRef = useRef();
  const { virtualRows, rowVirtualizer, notifications, isLoading, hasNotifications, selectedNotification, handleNotificationClick, handleClose, handleBack } = useNotificationsPanel(parentRef, open, onClose);

  const emptyState = useMemo(() => (
    <EmptyStateContainer>
      <EmptyStateIcon />
      <EmptyStateTitle color="grey.700">{EMPTY_STATE_TITLE}</EmptyStateTitle>
      <EmptyStateSubtitle color="grey.500">{EMPTY_STATE_SUBTITLE}</EmptyStateSubtitle>
    </EmptyStateContainer>
  ), []);

  const loadingState = useMemo(() => (
    <>
      <Skeleton variant="rectangular" height={24} width={100} />
      {Array.from({ length: 5 }, (_, index) => (
        <NotificationItemSkeleton key={index} />
      ))}
    </>
  ), []);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{ zIndex: theme.zIndex.drawer + 2 }}
    >
      <PanelContainer>
        <Header>
          {!selectedNotification && <Title data-testid="notifications-panel-title">{NOTIFICATIONS_PANEL_TITLE}</Title>}
          {selectedNotification && <HyroButton
            sx={{
              transform: "translateX(-2px)",
              paddingInline: "12px"
            }}
            startIcon={<ChevronLeftIcon />}
            color={BUTTON_COLORS.SECONDARY}
            onClick={handleBack}
          >Back
          </HyroButton>}
          <HyroIconButton variant={ICON_BUTTON_VARIANTS.CLEAR} size={ICON_BUTTON_SIZES.LARGE} onClick={handleClose} data-testid="notifications-panel-close-button">
            <CloseIcon />
          </HyroIconButton>
        </Header>

        <Content ref={parentRef} data-testid="notifications-panel-content" isLoading={isLoading}>
          {!hasNotifications && !isLoading && emptyState}

          {isLoading && loadingState}

          {!isLoading && hasNotifications && (
            <>
              {selectedNotification && <NotificationDetailedView notification={selectedNotification} />}

              {!selectedNotification && <VirtualizedListContainer height={rowVirtualizer.getTotalSize()} >
                {notifications.map((virtualRow) => {
                  const row = virtualRows[virtualRow.index];

                  return (
                    <VirtualizedRow key={row.key} top={virtualRow.start} height={virtualRow.size}>
                      {row.type === SECTION_TYPE.HEADER ? (
                        <VirtualizedHeader>{row.title}</VirtualizedHeader>
                      ) : row.type === SECTION_TYPE.LOADING ? (
                        <NotificationItemSkeleton />
                      ) : (
                        row.notification && <NotificationItem {...row.notification} onClick={() => handleNotificationClick(row.notification)} />
                      )}
                    </VirtualizedRow>
                  );
                })}
              </VirtualizedListContainer>
              }
            </>
          )}
        </Content>
      </PanelContainer>
    </Drawer >
  );
};

export default NotificationsPanel;
